'use client';

import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import ArrowSVG from '../../public/img/angle-right.svg';
import CategoryBlock from './CategoryFilter';
import { H1 } from '../styled/typography';
import SortFilter from './SortFilter';
import TimeFilter from './TimeFilter';
import { CropText } from '../collapsable/collapsable';
import {
  DEVICE_SCREENS_MIN_STRING,
  filterDateArr,
  filterDates,
  filterPriceArr,
} from '../../lib/consts';
import {
  ICostFilter,
  FilterQuery,
  FilterTitles,
  ISortFilter,
} from '../../models/filters';
import { Category, CategoryData } from '../../models/action';
import CostFilter from './CostFilter';
import wordDeclension from '../../utils/word-declension';
import { UpButton } from '../common/UpButton';
import { City } from '../../models';
import { useCookieAgreement } from '../../providers';

const mainList = [
  { id: 1, page: 'category', title: 'Категория' },
  { id: 2, page: 'sort', title: 'Сортировка' },
  { id: 3, page: 'cost', title: 'Стоимость' },
  { id: 4, page: 'time', title: 'Когда' },
];

interface CatalogFilterProps {
  currentCity: City;
  query: FilterQuery;
  setQuery: (query: FilterQuery) => void;
  setShowMobileSort: (visible: boolean) => void;
  categories: Category[];
  sortTypes: ISortFilter[];
  defaultQuery: FilterQuery;
  eventCount: number;
  seoCategory: CategoryData | null;
  setSeoCategory: Dispatch<SetStateAction<CategoryData | null>>;
}

interface FiltersData {
  sort: ISortFilter | null;
  cost: ICostFilter | null;
  time: number | null;
}

export default function CatalogFilter({
  currentCity,
  query,
  setQuery,
  setShowMobileSort,
  categories,
  sortTypes,
  defaultQuery,
  eventCount,
  seoCategory,
  setSeoCategory,
}: CatalogFilterProps) {
  const { showPopUp } = useCookieAgreement();
  const [filterPage, setFilterPage] = useState('');
  const [showHideFiltersButton, setShowHideFiltersButton] = useState(false);
  const [filterTitles, setFilterTitles] = useState<FilterTitles>({
    category: null,
    sort: null,
    cost: null,
    time: null,
  });
  const [filtersData, setFiltersData] = useState<FiltersData>({
    sort: null,
    cost: null,
    time: null,
  });

  useEffect(() => {
    const {
      filter_price__gte: queryPriceFrom,
      filter_price__lte: queryPriceTo,
      date_from: dateFrom,
      date_to: dateTo,
      categories: { category: categoryId, subcategories: subcategoriesId },
    } = query.filters;

    const category = categories.find((c) => c.id === categoryId);
    const subcategories = categories.map((c) => c.children).flat();
    const checkedSubcategories = subcategoriesId.map((sub) =>
      subcategories.find((item) => item.id === sub)
    );
    setShowHideFiltersButton(!!category || !!checkedSubcategories.length);
    let categoryTitle = '';
    if (category && checkedSubcategories.length) {
      categoryTitle = `${category?.title}: ${checkedSubcategories
        .map((sub) => sub?.title || '')
        .join(', ')}`;
    } else if (category) {
      categoryTitle = category.title;
    }

    let costFilter: ICostFilter | null = null;
    if (
      typeof queryPriceFrom !== 'object' &&
      typeof queryPriceTo !== 'object'
    ) {
      costFilter =
        filterPriceArr.find(
          (item) =>
            item.price1 === Number(queryPriceFrom) &&
            item.price2 === Number(queryPriceTo)
        ) || null;
    }

    const sortFilter =
      sortTypes?.find((item) => item.slug === query.filters.sort) || null;

    let timeIndex: number | null = null;
    let timeTitle = '';

    if (typeof dateFrom !== 'object' && typeof dateTo !== 'object') {
      timeIndex = Array.from({
        ...filterDates,
        length: Object.keys(filterDateArr).length,
      }).findIndex((item: any) => item[0] === dateFrom && item[1] === dateTo);

      if (timeIndex !== -1 && timeIndex !== null) {
        timeTitle = filterDateArr[timeIndex].title;
      } else if (timeIndex) {
        const reverseDateFrom = DateTime.fromFormat(
          dateFrom,
          'yyyy-LL-dd'
        ).toFormat('dd.LL.yyyy');
        const reverseDateTo = DateTime.fromFormat(
          dateTo,
          'yyyy-LL-dd'
        ).toFormat('dd.LL.yyyy');

        timeTitle = `${reverseDateFrom} - ${reverseDateTo}`;
      }
    }

    setFilterTitles({
      category: categoryTitle,
      cost: costFilter?.title || null,
      sort: sortFilter?.title || null,
      time: timeTitle,
    });
    setFiltersData({
      sort: sortFilter,
      cost: costFilter,
      time: timeIndex,
    });
  }, [query]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  return (
    <div
      css={{
        padding: '15px 8px',
        [DEVICE_SCREENS_MIN_STRING.md]: {
          width: '66%',
        },
      }}
    >
      {!filterPage && (
        <>
          <H1
            css={{
              fontFamily: 'Montserrat',
              fontSize: '24px !important',
              fontWeight: 900,
            }}
          >
            Фильтры и сортировка
          </H1>

          <div
            css={{
              display: 'flex',
              flexDirection: 'column',
              marginTop: 14,
              paddingRight: 8,
            }}
          >
            {mainList.map((item) => {
              if (item.page === 'cost' && !currentCity.country.is_default) {
                return null;
              }
              return (
                <button
                  type='button'
                  key={item.id}
                  onClick={() => setFilterPage(item.page)}
                  css={{
                    width: '100%',
                    height: 75,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: 18,
                    fontWeight: 700,
                    color: '#333',
                    textAlign: 'left',
                    fontFamily: 'Montserrat',
                    boxShadow: '0 1px 1px rgba(0, 0, 0, 0.05)',
                    ':first-child': {
                      boxShadow:
                        '0 1px 1px rgba(0, 0, 0, 0.05), 0 -1px 0 rgba(0, 0, 0, 0.05)',
                    },
                  }}
                >
                  <span
                    css={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                    }}
                  >
                    {filterTitles[item.page] ? (
                      <>
                        <span
                          css={{
                            fontFamily: 'Montserrat',
                            lineHeight: '28px',
                            fontSize: 18,
                            letterSpacing: '0em',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '90vw',
                          }}
                        >
                          {filterTitles[item.page]}
                        </span>
                        <span
                          css={{
                            fontSize: 12,
                            fontWeight: 300,
                            fontFamily: '"Golos", Arial',
                          }}
                        >
                          {item.title}
                        </span>
                      </>
                    ) : (
                      <span>{item.title}</span>
                    )}
                  </span>

                  <ArrowSVG css={{ fill: '#C7C7CC' }} />
                </button>
              );
            })}
          </div>
        </>
      )}

      {filterPage === 'category' && (
        <CategoryBlock
          categories={categories}
          eventCount={eventCount}
          query={query}
          setFilterTitles={setFilterTitles}
          setSeoCategory={setSeoCategory}
          setQuery={setQuery}
        />
      )}

      {filterPage === 'sort' && (
        <SortFilter
          query={query}
          setQuery={setQuery}
          sortTypes={sortTypes}
          filtersData={filtersData.sort}
        />
      )}

      {filterPage === 'cost' && (
        <CostFilter
          query={query}
          setQuery={setQuery}
          filtersData={filtersData.cost}
        />
      )}

      {filterPage === 'time' && (
        <TimeFilter
          query={query}
          setQuery={setQuery}
          filtersData={filtersData.time}
        />
      )}

      <div
        css={{
          display: 'flex',
          marginTop: 16,
          flexDirection: 'column',
          gap: 15,
        }}
      >
        {!!filterPage && (
          <button
            type='button'
            onClick={() => setFilterPage('')}
            css={{
              height: 40,
              color: '#f44336',
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            Назад
          </button>
        )}

        {showHideFiltersButton && (
          <button
            type='button'
            css={{
              position: 'fixed',
              left: 15,
              right: 15,
              bottom: showPopUp ? 67 : 15,
              zIndex: 999,
              height: 40,
              color: '#fff',
              borderRadius: 30,
              boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.25)',
              fontFamily: 'Montserrat',
              fontSize: 15,
              fontWeight: 600,
              backgroundColor: '#ff4a2e',
              backgroundImage:
                'linear-gradient(120deg, #ff4a2e 0%, #de2839 100%)',
              backgroundSize: '500% 500%',
              animation: 'move-background 60s linear infinite',
              transition: 'all 0.2s ease, box-shadow 0.4s ease',
            }}
            onClick={() => setShowMobileSort(false)}
          >
            Показать {eventCount}{' '}
            {wordDeclension(eventCount, 'событие', 'события', 'событий')}
          </button>
        )}

        <button
          type='button'
          css={{
            height: 40,
            color: '#f44336',
            fontSize: 13,
            fontWeight: 500,
          }}
          onClick={() => setQuery(defaultQuery)}
        >
          Сбросить
        </button>
      </div>

      {seoCategory && (
        <div
          css={{
            padding: '16px 8px 0 8px',
          }}
        >
          <CropText
            text={seoCategory.description}
            deskTopCount={1000}
            mobileCount={500}
          />
        </div>
      )}

      <UpButton mobileScreenCount={0.5} />
    </div>
  );
}
